import { useEffect, useState } from 'react'
import { api } from '@/api'
import type { AssetLibraryStats } from '@/api/types'

/**
 * 资产库统计（作品数 / 素材数 / 收藏数），**一律以后端库为准**。
 *
 * ===========================================================================
 * 为什么要有这个 hook
 * ===========================================================================
 * 首页那四张统计卡里有两张（作品总数、素材库）原来读的是**浏览器镜像**：
 *   · `WorksContext`（`hw_mirror_works_v1`）
 *   · `AssetLibraryContext`（`hw_mirror_assets_v1`）
 * 那两个镜像是为了「后端没起时界面不空」而留的降级缓存，**不是真源**。
 * 拿它当计数来源的后果：用户清一次站点数据，首页就从「88 个作品」变成「0 个」——
 * 而库里的行一条没少。这正是 2026-09-29 报障「清空了就没」的那类现象，
 * 只不过发生在计数上：**数字归零 ≠ 数据消失，但用户分不出这两件事**。
 *
 * 所以计数走接口（`GET /api/asset-library/stats`），镜像是镜像、数字是数字。
 *
 * 取不到时返回 `null`（而不是 0）：界面显示「—」，用户知道「没读到」，
 * 不会把 0 当成「东西没了」。
 */
export function useLibraryStats(): AssetLibraryStats | null {
  const [stats, setStats] = useState<AssetLibraryStats | null>(null)

  useEffect(() => {
    let alive = true
    api
      .assetLibraryStats()
      .then((s) => {
        if (alive) setStats(s)
      })
      .catch(() => {
        // 后端不可达：保持 null（界面显示「—」），绝不回落到本地镜像充数
        if (alive) setStats(null)
      })
    return () => {
      alive = false
    }
  }, [])

  return stats
}
